/**
 * A SHORT LIST OF NAMED FACTS. 09 §5.4 T12's sibling rule, one level down.
 *
 * A detail screen is a handful of labelled values, and the two ways it goes wrong are a table with
 * two columns (which acquires sorting, filtering and a totals row it has no use for) and a
 * paragraph (which cannot be scanned). A description list is neither, and it is what a screen reader
 * announces as a term and its definition.
 *
 * A `null` value is rendered as a written absence, never as an empty cell: 09 §9.2 keeps null
 * visually distinct from zero, and the same rule applies to a fact that has no value yet.
 */

import type { ReactNode } from 'react';

export interface Fact {
  readonly labelHe: string;
  /** `null` ⇒ the value does not exist, and the list says so in words. */
  readonly value: ReactNode | null;
  /** Shown when `value` is null. Defaults to the general written absence. */
  readonly absentHe?: string;
}

/*
 * THE LIST IS LAID OUT IN COLUMNS, AND THE LABEL SITS ABOVE ITS VALUE. **Owner, 2026-10-23 §4.**
 *
 * It was one full-width row per fact, label pinned to one edge and value to the other, so the
 * customer card read as six long rules across the screen with a gulf in the middle of each — the
 * *"שדות רחבים מאוד"* of the finding, and an eye that travels the width of the page per value.
 *
 * `facts--columns` puts each pair in one cell and flows the cells into at most three columns on a
 * wide screen and exactly one when narrow. It is the DEFAULT rather than an option, because §4
 * asks for a CONSISTENT structure: a second layout here, chosen per screen, is how the four
 * screens stopped looking like one product in the first place.
 */
export function FactList({ facts }: { readonly facts: readonly Fact[] }): JSX.Element {
  return (
    <dl className="facts facts--columns">
      {facts.map((fact) => (
        <div key={fact.labelHe} className="facts__row">
          <dt className="facts__label">{fact.labelHe}</dt>
          <dd className="facts__value">
            {fact.value === null ? (
              <span className="facts__absent">{fact.absentHe ?? 'אין נתון'}</span>
            ) : (
              fact.value
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}
